import { request } from "./client";
import type { StaffAuth } from "./teaching";
import type { DemoRequestStatus } from "../types/prisma-enums";

/**
 * F6 — the Connect page's wire, and the operator's list of what it produced.
 *
 * 🔴 ONE PUBLIC WRITE AND ONE OPERATOR READ, IN ONE FILE, BECAUSE THEY ARE TWO
 * HALVES OF ONE FEATURE — and the backend keeps them in two controller classes
 * for the reason the authorisation is asymmetric. Nothing here stores a token:
 * `submitDemoRequest` sends none, and the reads take the caller's, exactly as
 * `platform.ts` does.
 *
 * 🔴 A ROW HERE IS NOT AN ACCOUNT. `admin-panel.md` §3 — *"a school cannot mint
 * its own first admin"* — is why the public site asks a school to get in touch
 * rather than to register, and this endpoint is the getting in touch. It creates
 * no School, no User and no Subscription; an operator reads the list and
 * onboards them by hand.
 */

/** The four the form offers. Closed, because the checkboxes are ours. */
export const INTERESTS = [
  "STUDENT_ENGAGEMENT",
  "LEARNING_OUTCOMES",
  "ASSESSMENT_TRACKING",
  "TEACHER_SUPPORT",
] as const;

export type Interest = (typeof INTERESTS)[number];

export interface DemoRequestBody {
  name: string;
  designation: string;
  email: string;
  phone: string;
  schoolName: string;
  schoolType: string;
  board: string;
  city: string;
  state: string;
  studentRange: string;
  hearAbout?: string;
  interests?: Interest[];
}

export interface DemoRequestRow extends Required<
  Omit<DemoRequestBody, "hearAbout" | "interests">
> {
  id: string;
  hearAbout: string | null;
  interests: string[];
  status: DemoRequestStatus;
  /** ISO — the wire carries strings; the screen formats them. */
  contactedAt: string | null;
  createdAt: string;
}

export interface DemoRequestPage {
  rows: DemoRequestRow[];
  nextCursor: string | null;
  /** `NEW` only, counted over the whole table rather than the page. */
  waiting: number;
}

/**
 * 🔴 PUBLIC, AND THE RESPONSE IS AN ACKNOWLEDGEMENT RATHER THAN THE ROW. The
 * server echoes nothing back — no id, no fields — so there is nothing here to
 * render into the page that a stranger typed.
 */
export const submitDemoRequest = (
  body: DemoRequestBody,
  signal?: AbortSignal,
): Promise<{ recorded: true }> =>
  request<{ recorded: true }>("/demo-requests", {
    method: "POST",
    body,
    context: "public",
    signal,
  });

export const getDemoRequests = (
  query: { limit?: number; cursor?: string; status?: DemoRequestStatus } = {},
  auth: StaffAuth = {},
): Promise<DemoRequestPage> => {
  // The schema is `.strict()`, so an empty value is a 400 rather than an
  // ignored parameter — `getFeedbackPage`'s rule.
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined && query.cursor.length > 0)
    params.set("cursor", query.cursor);
  if (query.status !== undefined) params.set("status", query.status);
  const qs = params.toString();
  return request<DemoRequestPage>(
    `/superadmin/demo-requests${qs.length > 0 ? `?${qs}` : ""}`,
    {
      context: "user",
      token: auth.token,
      signal: auth.signal,
    },
  );
};

/**
 * ⚠️ THE STATUS IS THE ONLY WRITABLE FIELD. What a school typed is evidence of
 * what a school asked for; an operator who could edit it could make the record
 * disagree with the conversation it produced.
 */
export const setDemoRequestStatus = (
  id: string,
  status: DemoRequestStatus,
  auth: StaffAuth = {},
): Promise<DemoRequestRow> =>
  request<DemoRequestRow>(
    `/superadmin/demo-requests/${encodeURIComponent(id)}/status`,
    {
      method: "PATCH",
      body: { status },
      context: "user",
      token: auth.token,
      signal: auth.signal,
    },
  );

/* ── Operator lead writes (B1 pipeline, K3) — PKG-SALES contract ─────────── */

/** Where a lead came from. Website enquiries are created by the public form only. */
export type LeadSource = "WEBSITE" | "MANUAL" | "REFERRAL" | "EVENT";

/** B1 "New lead" — an operator entering a school by hand (`sales.manage`, audited LEAD_CREATED). */
export interface NewLeadBody {
  name: string;
  designation?: string;
  email: string;
  phone: string;
  schoolName: string;
  board?: string;
  city: string;
  state: string;
  studentRange: string;
  teacherRange?: string;
  source: Exclude<LeadSource, "WEBSITE">;
  message?: string;
  ownerId?: string;
}

export const createLead = (
  body: NewLeadBody,
  auth: StaffAuth,
): Promise<{ id: string; number: string }> =>
  request<{ id: string; number: string }>("/superadmin/demo-requests", {
    method: "POST",
    body,
    context: "user",
    token: auth.token,
  });

/** Assign (or clear) the operator working a lead (`sales.manage`, audited LEAD_ASSIGNED). */
export const setLeadOwner = (
  id: string,
  ownerId: string | null,
  auth: StaffAuth,
): Promise<unknown> =>
  request<unknown>(
    `/superadmin/demo-requests/${encodeURIComponent(id)}/owner`,
    { method: "PATCH", body: { ownerId }, context: "user", token: auth.token },
  );

/**
 * Book a demo and/or set the next action (B1 card "Fri 11:00 · Demo on
 * Friday"). Each key is optional; `null` clears it. ISO instants.
 */
export interface LeadSchedulePatch {
  demoAt?: string | null;
  nextActionAt?: string | null;
  nextActionNote?: string | null;
}

export const setLeadSchedule = (
  id: string,
  patch: LeadSchedulePatch,
  auth: StaffAuth,
): Promise<unknown> =>
  request<unknown>(
    `/superadmin/demo-requests/${encodeURIComponent(id)}/schedule`,
    { method: "PATCH", body: patch, context: "user", token: auth.token },
  );

/**
 * The lead's working status, with the decline / lost reason when it is closed
 * (stored as `closedReason`; cleared when the lead is reopened).
 */
export const setLeadStatusWithReason = (
  id: string,
  status: DemoRequestStatus,
  reason: string | null,
  auth: StaffAuth,
): Promise<unknown> =>
  request<unknown>(
    `/superadmin/demo-requests/${encodeURIComponent(id)}/status`,
    {
      method: "PATCH",
      body: reason && status === "CLOSED" ? { status, reason } : { status },
      context: "user",
      token: auth.token,
    },
  );
